import { Injectable, signal } from '@angular/core';

import { IconName } from '@shared/components/icon/icon.component';

export interface PaletteCommand {
  /** Stable id for tracking and for highlight-on-arrow-key. */
  id: string;
  /** Primary label rendered in the palette. */
  label: string;
  /** Optional secondary text — group, hint, or shortcut (e.g. "Navigation"). */
  hint?: string;
  /** Optional icon shown to the left. */
  icon?: IconName;
  /** Group name — entries with the same group are rendered together. */
  group: string;
  /** Search keywords beyond the label. Lowercased. */
  keywords?: readonly string[];
  /** Action to run when the entry is activated. */
  run: () => void;
  /** Permission required to show this command, e.g. 'Consignor:Create'. Omit to always show. */
  permission?: string;
}

/**
 * Global command palette state and registry. A handful of base commands are
 * registered at app boot (navigation jumps); features can call `register` to
 * contribute scoped commands while their routes are active.
 */
@Injectable({ providedIn: 'root' })
export class CommandPaletteService {
  private readonly _open = signal(false);
  private readonly _query = signal('');
  private readonly _commands = signal<readonly PaletteCommand[]>([]);

  readonly open = this._open.asReadonly();
  readonly query = this._query.asReadonly();
  readonly commands = this._commands.asReadonly();

  toggle(): void {
    this._open.update(o => !o);
    if (!this._open()) this._query.set('');
  }

  setOpen(open: boolean): void {
    this._open.set(open);
    if (!open) this._query.set('');
  }

  setQuery(value: string): void {
    this._query.set(value);
  }

  /** Register a list of commands. Returns an unregister function for scoped use. */
  register(commands: readonly PaletteCommand[]): () => void {
    this._commands.update(existing => [...existing, ...commands]);
    const ids = new Set(commands.map(c => c.id));
    return () => {
      this._commands.update(existing => existing.filter(c => !ids.has(c.id)));
    };
  }
}
